import React, { useMemo, useRef } from 'react';
import jsPDF from 'jspdf';
import { decodeSharedReceiptPayload } from '../lib/receiptShare';

interface ReceiptSharePageProps {
  encodedData: string;
}

const ReceiptSharePage: React.FC<ReceiptSharePageProps> = ({ encodedData }) => {
  const receiptRef = useRef<HTMLDivElement>(null);
  const payload = useMemo(() => decodeSharedReceiptPayload(encodedData), [encodedData]);

  const handleDownloadImage = async () => {
    if (!receiptRef.current) return;
    const [{ default: html2canvas }] = await Promise.all([import('html2canvas')]);
    const canvas = await html2canvas(receiptRef.current, { backgroundColor: '#ffffff', scale: 2 });
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `receipt-${payload?.orderId ?? 'order'}.png`;
    link.click();
  };

  const handleDownloadPdf = async () => {
    if (!receiptRef.current) return;
    const [{ default: html2canvas }] = await Promise.all([import('html2canvas')]);
    const canvas = await html2canvas(receiptRef.current, { backgroundColor: '#ffffff', scale: 2 });
    const img = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(img, 'PNG', 10, 10, imgWidth, imgHeight);
    pdf.save(`receipt-${payload?.orderId ?? 'order'}.pdf`);
  };

  if (!payload) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-6 max-w-md text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Receipt link is invalid</h1>
          <p className="text-gray-600">Please ask staff to generate a new receipt QR code.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-md mx-auto space-y-4">
        <div ref={receiptRef} className="bg-white rounded-lg shadow p-5">
          <h1 className="text-xl font-bold text-gray-900">{payload.businessName || 'Receipt'}</h1>
          <p className="text-sm text-gray-500">Order #{payload.orderId}</p>
          {(payload.tokenDisplay ||
            (typeof payload.tokenNumber === 'number' && payload.tokenNumber > 0)) && (
            <p className="text-sm font-semibold text-pink-700 mt-1">
              {payload.tokenDisplay
                ? payload.tokenDisplay
                : `Token #${payload.tokenNumber}`}
            </p>
          )}
          <p className="text-sm text-gray-500 mb-3">{new Date(payload.createdAt).toLocaleString()}</p>
          {payload.customerName && <p className="text-sm text-gray-700 mb-3">Customer: {payload.customerName}</p>}
          {payload.subBillId && (
            <p className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-3">
              Sub-bill: {payload.subBillName || 'Guest'} — your portion of order #{payload.orderId}
            </p>
          )}
          <div className="border-t border-b py-2 space-y-2">
            {payload.items.map((item, index) => (
              <div key={`${item.name}-${index}`} className="flex items-start justify-between gap-2">
                <p className="text-sm text-gray-800">{item.name} x {item.quantity}</p>
                <p className="text-sm font-medium text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span className="text-gray-900">${payload.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">GST (incl.)</span>
              <span className="text-gray-900">${payload.taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-base pt-1">
              <span>Total</span>
              <span>${payload.totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 space-y-2">
          <button type="button" onClick={handleDownloadImage} className="w-full py-2 rounded-lg bg-pink-500 text-white font-semibold hover:bg-pink-600">
            Download as image
          </button>
          <button type="button" onClick={handleDownloadPdf} className="w-full py-2 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300">
            Download as PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptSharePage;
